import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp,
  TrendingDown,
  Lightbulb,
  FileText,
  Users,
  Send,
  Mail,
  MessageSquare,
  Calendar,
  CheckCircle,
  Eye,
} from "lucide-react";

interface Metrics {
  total_ideas: number;
  total_decks: number;
  total_investors: number;
  total_outreach: number;
  total_opens: number;
  total_replies: number;
  total_meetings: number;
  milestones_done: number;
  total_deck_views: number;
}

interface ChartDataPoint {
  date: string;
  new_ideas: number;
  new_outreach: number;
  new_milestones: number;
  new_deck_views: number;
}

interface MetricsKPICardsProps {
  metrics: Metrics;
  chartData: ChartDataPoint[];
}

export default function MetricsKPICards({ metrics, chartData }: MetricsKPICardsProps) {
  // Calculate 7-day trends
  const getLast7DaysTrend = (field: keyof ChartDataPoint) => {
    if (!chartData || chartData.length < 8) return 0;
    
    const last7Days = chartData.slice(-7);
    const previous7Days = chartData.slice(-14, -7);
    
    const last7Sum = last7Days.reduce((sum, d) => sum + (Number(d[field]) || 0), 0);
    const prev7Sum = previous7Days.reduce((sum, d) => sum + (Number(d[field]) || 0), 0);
    
    if (prev7Sum === 0) return last7Sum > 0 ? 100 : 0;
    return Math.round(((last7Sum - prev7Sum) / prev7Sum) * 100);
  };

  const ideasTrend = getLast7DaysTrend("new_ideas");
  const outreachTrend = getLast7DaysTrend("new_outreach");
  const milestonesTrend = getLast7DaysTrend("new_milestones");
  const viewsTrend = getLast7DaysTrend("new_deck_views");

  const kpis = [
    {
      title: "Total Ideas",
      value: metrics.total_ideas,
      icon: Lightbulb,
      trend: ideasTrend,
      color: "text-blue-500",
    },
    {
      title: "Pitch Decks",
      value: metrics.total_decks,
      icon: FileText,
      trend: 0,
      color: "text-purple-500",
    },
    {
      title: "Investors",
      value: metrics.total_investors,
      icon: Users,
      trend: 0,
      color: "text-green-500",
    },
    {
      title: "Outreach Sent",
      value: metrics.total_outreach,
      icon: Send,
      trend: outreachTrend,
      color: "text-orange-500",
    },
    {
      title: "Deck Opens",
      value: metrics.total_opens,
      icon: Mail,
      trend: 0,
      color: "text-cyan-500",
    },
    {
      title: "Replies",
      value: metrics.total_replies,
      icon: MessageSquare,
      trend: 0,
      color: "text-pink-500",
    },
    {
      title: "Meetings",
      value: metrics.total_meetings,
      icon: Calendar,
      trend: 0,
      color: "text-indigo-500",
    },
    {
      title: "Milestones Done",
      value: metrics.milestones_done,
      icon: CheckCircle,
      trend: milestonesTrend,
      color: "text-emerald-500",
    },
    {
      title: "Deck Views",
      value: metrics.total_deck_views,
      icon: Eye,
      trend: viewsTrend,
      color: "text-amber-500",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 print:grid-cols-5 print:gap-2">
      {kpis.map((kpi) => {
        const Icon = kpi.icon;
        const isPositive = kpi.trend > 0;
        const isNegative = kpi.trend < 0;
        
        return (
          <Card key={kpi.title} className="print:shadow-none">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 print:pb-1">
              <CardTitle className="text-sm font-medium print:text-xs">
                {kpi.title}
              </CardTitle>
              <Icon className={`h-4 w-4 ${kpi.color} print:h-3 print:w-3`} />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold print:text-lg">{kpi.value}</div>
              {kpi.trend !== 0 && (
                <div className="flex items-center gap-1 mt-1">
                  {isPositive && <TrendingUp className="h-3 w-3 text-green-500" />}
                  {isNegative && <TrendingDown className="h-3 w-3 text-red-500" />}
                  <span
                    className={`text-xs print:text-[10px] ${
                      isPositive ? "text-green-500" : isNegative ? "text-red-500" : "text-muted-foreground"
                    }`}
                  >
                    {kpi.trend > 0 ? "+" : ""}
                    {kpi.trend}% (7d)
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}